// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect } from 'react';
import { fetchWVStreams } from '../utils/streamService';
import { Map, Heart, Users, ArrowRight } from 'lucide-react';

const Home = ({ setCurrentPage }) => {
  const [stats, setStats] = useState({
    totalStreams: 0,
    totalCounties: 55,
    totalCatches: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const streams = await fetchWVStreams();
      setStats(prev => ({
        ...prev,
        totalStreams: streams.length
      }));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="hero">
        <div className="hero-badge">
          🎣 West Virginia's Premier Fishing Guide
        </div>
        <h1 className="hero-title">
          Find My <span className="trout">Trout</span>
        </h1>
        <p className="hero-subtitle">
          Discover the best trout streams in the Mountain State. Interactive maps,
          real-time weather updates, and a community of passionate anglers.
        </p>

        <div style={{ marginTop: '3rem' }}>
          <button className="btn btn-primary" onClick={() => setCurrentPage('map')}>
            <Map size={20} />
            Explore Map
          </button>
        </div>
      </div>

      {/* Features Section */}
      <div className="section">
        <h2 className="section-title">Everything You Need for a Great Day on the Water</h2>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon green">
              <Map size={28} />
            </div>
            <h3 className="feature-title">Interactive Map</h3>
            <p className="feature-description">
              Explore trout streams across all 55 West Virginia counties with real-time weather data
            </p>
            <a href="#" className="feature-link" onClick={(e) => { e.preventDefault(); setCurrentPage('map'); }}>
              Explore <ArrowRight size={16} />
            </a>
          </div>

          <div className="feature-card">
            <div className="feature-icon yellow">
              <Heart size={28} />
            </div>
            <h3 className="feature-title">My Favorites</h3>
            <p className="feature-description">
              Save and quickly access your favorite fishing spots
            </p>
            <a href="#" className="feature-link" onClick={(e) => { e.preventDefault(); setCurrentPage('favorites'); }}>
              Explore <ArrowRight size={16} />
            </a>
          </div>

          <div className="feature-card">
            <div className="feature-icon blue">
              <Users size={28} />
            </div>
            <h3 className="feature-title">Community</h3>
            <p className="feature-description">
              Share catches and connect with fellow anglers
            </p>
            <a href="#" className="feature-link" onClick={(e) => { e.preventDefault(); setCurrentPage('community'); }}>
              Explore <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-value">{stats.totalStreams > 0 ? `${stats.totalStreams}+` : 'Loading...'}</div>
            <div className="stat-label">Trout Streams</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.totalCounties}</div>
            <div className="stat-label">Counties</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.totalCatches}</div>
            <div className="stat-label">Catches Shared</div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <h2 className="cta-title">Ready to Find Your Perfect Spot?</h2>
        <p className="cta-description">
          Join thousands of West Virginia anglers who use Find My Trout to discover new fishing destinations.
        </p>
        <button className="btn btn-green" onClick={() => setCurrentPage('map')}>
          Start Exploring <ArrowRight size={18} />
        </button>
      </div>

      {/* Footer */}
      <div className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            Find My Trout
          </div>
          <div className="footer-text">
            Your guide to West Virginia's best trout fishing
          </div>
          <div className="footer-copyright">
            © 2026 Find My Trout
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
